import { computed, toValue, type MaybeRef, type Ref } from 'vue'

import { useRealtimeSubscription, type RealtimeFrame } from '@ja/realtime'

/**
 * CRUD real-time event payload structure.
 *
 * Wire events:
 * - `crud.created`
 * - `crud.updated`
 * - `crud.deleted`
 * - `crud.bulk_deleted`
 *
 * Channel: `crud:{modelName}` (list fanout).
 */
export interface CrudEvent {
  action: 'created' | 'updated' | 'deleted' | 'bulk_deleted'
  modelName: string
  id?: string | number
  ids?: Array<string | number>
  data?: Record<string, unknown>
  oldData?: Record<string, unknown>
  actorId: string | number | null
  actorName?: string | null
  timestamp: string
}

export interface UseCrudChannelOptions {
  onCreated?: (event: CrudEvent) => void
  onUpdated?: (event: CrudEvent) => void
  onDeleted?: (event: CrudEvent) => void
  onBulkDeleted?: (event: CrudEvent) => void
  onAny?: (event: CrudEvent) => void
  /**
   * Consumer (actor-id) dedup — drop frames where `event.actorId === currentUserId`.
   * Transport-layer (connection-id) dedup is automatic inside the Realtime adapter.
   */
  skipOwnEvents?: boolean
  currentUserId?: string | number | null
}

export interface UseCrudChannelReturn {
  isConnected: Ref<boolean>
  unsubscribe: () => void
}

const WIRE_EVENT_TO_ACTION: Record<string, CrudEvent['action']> = {
  'crud.created': 'created',
  'crud.updated': 'updated',
  'crud.deleted': 'deleted',
  'crud.bulk_deleted': 'bulk_deleted',
}

/**
 * Vue composable for subscribing to CRUD list-level realtime events.
 *
 * Subscribes to `crud:<modelName>` via the shared Realtime primitive. For
 * per-record Editor/Viewer subscriptions use `useEditorRealTime` /
 * `useViewerRealTime` which bind to `crud:<model>:<id>`.
 */
export function useCrudChannel(
  modelName: MaybeRef<string>,
  options: UseCrudChannelOptions = {},
): UseCrudChannelReturn {
  const channel = computed(() => {
    const name = toValue(modelName)
    return name ? `crud:${name}` : ''
  })

  const {
    onCreated,
    onUpdated,
    onDeleted,
    onBulkDeleted,
    onAny,
    skipOwnEvents = false,
    currentUserId,
  } = options

  function dispatch(event: CrudEvent): void {
    if (
      skipOwnEvents &&
      currentUserId !== undefined &&
      currentUserId !== null &&
      event.actorId === currentUserId
    ) {
      return
    }

    switch (event.action) {
      case 'created':
        onCreated?.(event)
        break
      case 'updated':
        onUpdated?.(event)
        break
      case 'deleted':
        onDeleted?.(event)
        break
      case 'bulk_deleted':
        onBulkDeleted?.(event)
        break
    }

    onAny?.(event)
  }

  const { isConnected, unsubscribe } = useRealtimeSubscription<CrudEvent>(channel, {
    onMessage(frame: RealtimeFrame<CrudEvent>) {
      const action = WIRE_EVENT_TO_ACTION[frame.event]
      if (action === undefined) return

      const event: CrudEvent = { ...(frame.data as CrudEvent), action }
      dispatch(event)
    },
  })

  return { isConnected, unsubscribe }
}
